/**
 * Analytics Service
 * Centralized service for robust Google Analytics 4 tracking with attribution persistence.
 * Works alongside Intercom integration for unified cross-platform analytics.
 */

import { intercomService } from "./intercomService";
import configService from "./configService";
import posthogService from "./posthogService";
import { TrackingEvents, type TrackingEventName } from "./trackingEvents";
import {
  sanitizeAdminCustomerViewTelemetryString,
  trySanitizeAdminCustomerViewTelemetryValue,
} from "../utils/adminCustomerViewTelemetry";
import { isCustomerViewBootProfile } from "../utils/adminCustomerViewBoot";

export type CtaIntent = "signup" | "login";

export type CtaClickPayload = {
  campaign: string;
  label: string;
  section: string;
  destination: string;
  intent?: CtaIntent;
  /** Homepage version for shared CTA events, including alternate homepage routes. */
  pageVersion?: string;
};

export const ctaIntentFromCampaign = (campaign: string): CtaIntent => {
  const key = campaign.toLowerCase();
  if (key.includes("login") || key.includes("dashboard")) return "login";
  return "signup";
};

// Storage keys for attribution data
const STORAGE_KEYS = {
  FIRST_TOUCH: "plutus_first_touch_attribution",
  LAST_TOUCH: "plutus_last_touch_attribution",
  SESSION_START: "plutus_session_start",
  SIGNUP_START_TIME: "plutus_signup_start_time",
  LOGIN_START_TIME: "plutus_login_start_time",
} as const;

// UTM parameters to capture
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

// Page type mapping for content grouping
const PAGE_TYPE_MAP: Record<string, { type: string; group: string }> = {
  "/": { type: "home", group: "marketing" },
  "/providers": { type: "providers", group: "marketing" },
  "/llms": { type: "llms", group: "marketing" },
  "/login": { type: "login", group: "auth" },
  "/signup": { type: "signup", group: "auth" },
  "/share": { type: "share", group: "marketing" },
  "/picks": { type: "picks", group: "marketing" },
  "/positions": { type: "positions", group: "app" },
  "/alpha": { type: "alpha", group: "app" },
  "/provider": { type: "provider_portal", group: "app" },
  "/admin": { type: "admin", group: "admin" },
};

// Attribution data interface
export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
  timestamp?: string;
}

// User properties for GA4
export interface UserProperties {
  user_role?: string;
  is_provider?: boolean;
  is_admin?: boolean;
  provider_id?: string;
  signup_date?: string;
  account_age_days?: number;
  is_internal?: boolean;
}

// Page context for enhanced page views
export interface PageContext {
  page_type: string;
  content_group: string;
  is_authenticated: boolean;
  user_role?: string;
}

// Extend Window interface to include gtag and dataLayer
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Analytics Service Class
 * Provides comprehensive GA4 tracking with attribution persistence and Intercom integration.
 */
class AnalyticsService {
  private isInitialized = false;
  private gaMeasurementId: string | null = null;

  /**
   * Initialize the analytics service
   */
  initialize(): void {
    if (this.isInitialized) return;

    this.gaMeasurementId = configService.getGtagMeasurementId() || null;
    
    // Dynamically inject GA script if measurement ID is available
    // This ensures GA works locally (where the HTML placeholder isn't replaced)
    this.injectGAScript();
    
    // Capture attribution on initialization
    this.captureAttribution();
    
    // Track session start
    this.trackSessionStart();
    
    this.isInitialized = true;
  }

  /**
   * Dynamically inject GA4 script if not already loaded
   * This handles local development where the HTML placeholder isn't replaced
   */
  private injectGAScript(): void {
    if (typeof window === "undefined" || !this.gaMeasurementId) return;

    // Check if GA is already properly initialized (not with placeholder)
    const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag"]');
    if (existingScript) {
      const src = existingScript.getAttribute("src") || "";
      // If script exists with a valid measurement ID (not placeholder), skip injection
      if (src.includes("G-") && !src.includes("__REACT_APP")) {
        return;
      }
      // Remove the placeholder script
      existingScript.remove();
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    window.gtag = function(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    
    // Set initial timestamp
    window.gtag("js", new Date());
    
    // Configure GA4
    window.gtag("config", this.gaMeasurementId, {
      send_page_view: false,
      debug_mode: configService.isDevelopment() || configService.isLocal(),
    });

    // Inject the gtag.js script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaMeasurementId}`;
    document.head.appendChild(script);

    console.log(`[Analytics] GA4 initialized with ID: ${this.gaMeasurementId}`);
  }

  /**
   * Get the gtag function safely. Every runtime GA call is sanitized first.
   * Returns false when the call cannot be sent (missing gtag or sanitize failure).
   */
  private gtag(...args: unknown[]): boolean {
    const sanitized = trySanitizeAdminCustomerViewTelemetryValue(args);
    if (sanitized === null) return false;
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag(...sanitized);
      return true;
    }
    return false;
  }

  emitSanitizedGtag(...args: unknown[]): boolean {
    return this.gtag(...args);
  }

  /**
   * Extract GA Client ID from the _ga cookie
   * Format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
   */
  getGAClientId(): string | null {
    if (typeof document === "undefined") return null;

    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_ga" && value) {
        // Extract the client ID portion (last two parts)
        const parts = value.split(".");
        if (parts.length >= 4) {
          return `${parts[2]}.${parts[3]}`;
        }
      }
    }
    return null;
  }

  /**
   * Extract UTM parameters from the current URL
   */
  private extractUTMParams(): Partial<Record<(typeof UTM_PARAMS)[number], string>> {
    if (typeof window === "undefined") return {};

    const params: Partial<Record<(typeof UTM_PARAMS)[number], string>> = {};
    const urlParams = new URLSearchParams(window.location.search);

    for (const param of UTM_PARAMS) {
      const value = urlParams.get(param);
      if (value) {
        params[param] = value;
      }
    }

    return params;
  }

  /**
   * Capture and persist attribution data
   * First-touch: stored in localStorage (never overwritten)
   * Last-touch: stored in sessionStorage (updated each visit)
   */
  captureAttribution(): void {
    if (typeof window === "undefined") return;
    if (isCustomerViewBootProfile()) return;

    const utmParams = this.extractUTMParams();
    const referrer = document.referrer
      ? sanitizeAdminCustomerViewTelemetryString(document.referrer)
      : undefined;
    const landingPage = sanitizeAdminCustomerViewTelemetryString(
      window.location.pathname + window.location.search
    );
    const timestamp = new Date().toISOString();

    const attributionData: AttributionData = {
      ...utmParams,
      referrer,
      landing_page: landingPage,
      timestamp,
    };

    // Only store if we have meaningful attribution data
    const hasAttribution = Object.keys(utmParams).length > 0 || referrer;

    if (hasAttribution) {
      // First-touch: only set if not already present
      const existingFirstTouch = localStorage.getItem(STORAGE_KEYS.FIRST_TOUCH);
      if (!existingFirstTouch) {
        localStorage.setItem(STORAGE_KEYS.FIRST_TOUCH, JSON.stringify(attributionData));
      }

      // Last-touch: always update
      sessionStorage.setItem(STORAGE_KEYS.LAST_TOUCH, JSON.stringify(attributionData));
    }
  }

  /**
   * Get first-touch attribution data
   */
  getFirstTouchAttribution(): AttributionData | null {
    if (typeof localStorage === "undefined") return null;
    
    const data = localStorage.getItem(STORAGE_KEYS.FIRST_TOUCH);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Get last-touch attribution data
   */
  getLastTouchAttribution(): AttributionData | null {
    if (typeof sessionStorage === "undefined") return null;
    
    const data = sessionStorage.getItem(STORAGE_KEYS.LAST_TOUCH);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Get combined attribution data with prefixes for GA events
   */
  getAttributionParams(): Record<string, string | undefined> {
    const firstTouch = this.getFirstTouchAttribution();
    const lastTouch = this.getLastTouchAttribution();

    const params: Record<string, string | undefined> = {};

    // First-touch params
    if (firstTouch) {
      params.first_touch_source = firstTouch.utm_source;
      params.first_touch_medium = firstTouch.utm_medium;
      params.first_touch_campaign = firstTouch.utm_campaign;
      params.first_touch_content = firstTouch.utm_content;
      params.first_touch_term = firstTouch.utm_term;
      params.first_touch_referrer = firstTouch.referrer;
      params.first_touch_landing = firstTouch.landing_page;
    }

    // Last-touch params
    if (lastTouch) {
      params.last_touch_source = lastTouch.utm_source;
      params.last_touch_medium = lastTouch.utm_medium;
      params.last_touch_campaign = lastTouch.utm_campaign;
      params.last_touch_content = lastTouch.utm_content;
      params.last_touch_term = lastTouch.utm_term;
      params.last_touch_referrer = lastTouch.referrer;
    }

    return params;
  }

  /**
   * Track session start
   */
  private trackSessionStart(): void {
    if (typeof sessionStorage === "undefined") return;

    const existingSession = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    if (!existingSession) {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_START, new Date().toISOString());
    }
  }

  /**
   * Get page context based on pathname
   */
  getPageContext(pathname: string, isAuthenticated: boolean, userRole?: string): PageContext {
    // Find matching page type
    let pageType = "other";
    let contentGroup = "other";

    for (const [path, config] of Object.entries(PAGE_TYPE_MAP)) {
      if (pathname === path || pathname.startsWith(path + "/")) {
        pageType = config.type;
        contentGroup = config.group;
        break;
      }
    }

    return {
      page_type: pageType,
      content_group: contentGroup,
      is_authenticated: isAuthenticated,
      user_role: userRole,
    };
  }

  /**
   * Set user ID in GA4 (call after authentication)
   */
  setUserId(userId: string): void {
    if (!this.gaMeasurementId) return;

    this.gtag("config", this.gaMeasurementId, {
      update: true,
      send_page_view: false,
      user_id: userId,
    });
  }

  /**
   * Set user properties in GA4
   */
  setUserProperties(properties: UserProperties): void {
    this.gtag("set", "user_properties", properties);
  }

  /**
   * Track enhanced page view with rich context
   */
  trackPageView(
    url: string,
    title: string,
    context: PageContext,
    pageLocation?: string
  ): void {
    if (!this.gaMeasurementId) return;
    const sanitizedPath = sanitizeAdminCustomerViewTelemetryString(url);
    const absoluteLocation = pageLocation
      ?? (typeof window !== "undefined"
        ? `${window.location.origin}${sanitizedPath}`
        : sanitizedPath);
    const configured = this.gtag("config", this.gaMeasurementId, {
      update: true,
      send_page_view: false,
      page_location: absoluteLocation,
    });
    if (!configured) return;
    this.gtag("event", "page_view", {
      page_path: sanitizedPath,
      page_title: title,
      page_location: absoluteLocation,
      page_referrer: typeof document !== "undefined"
        ? sanitizeAdminCustomerViewTelemetryString(document.referrer || "")
        : "",
      page_type: context.page_type,
      content_group: context.content_group,
      is_authenticated: context.is_authenticated,
      user_role: context.user_role || "anonymous",
    });
  }

  /**
   * Track custom event to both GA and Intercom
   */
  trackEvent(
    eventName: string,
    params: Record<string, unknown> = {},
    intercomEventName?: string
  ): void {
    // Track to GA
    this.gtag("event", eventName, params);

    // Track to Intercom if event name provided
    if (intercomEventName) {
      intercomService.trackEvent(intercomEventName as any, {
        ga_event_name: eventName,
        ...params,
      });
    }
  }

  // ===================
  // SIGNUP FUNNEL EVENTS
  // ===================

  /**
   * Track signup funnel start (page load)
   */
  trackSignupStart(): void {
    // Store start time for conversion time calculation
    sessionStorage.setItem(STORAGE_KEYS.SIGNUP_START_TIME, new Date().toISOString());

    const attribution = this.getAttributionParams();

    this.gtag("event", "signup_start", {
      event_category: "conversion_funnel",
      ...attribution,
    });

    intercomService.trackEvent("signup_started" as any, {
      ...attribution,
      timestamp: new Date().toISOString(),
    });

    posthogService.capture(TrackingEvents.AUTH_SIGNUP_START, attribution);
  }

  /**
   * Track signup email submitted
   */
  trackSignupEmailSubmitted(email: string): void {
    const emailDomain = email.split("@")[1] || "unknown";

    this.gtag("event", "signup_email_submitted", {
      event_category: "conversion_funnel",
      email_domain: emailDomain,
    });

    intercomService.trackEvent("signup_email_submitted" as any, {
      email_domain: emailDomain,
      timestamp: new Date().toISOString(),
    });

    posthogService.capture(TrackingEvents.AUTH_SIGNUP_EMAIL_SUBMITTED, {
      email_domain: emailDomain,
    });
  }

  /**
   * Track signup completion with the method that completed the sign-in.
   */
  trackSignupComplete(userId: string, method: "email_otp" | "google_sso" = "email_otp"): void {
    const startTime = sessionStorage.getItem(STORAGE_KEYS.SIGNUP_START_TIME);
    const timeToConvert = startTime
      ? Math.round((Date.now() - new Date(startTime).getTime()) / 1000)
      : undefined;

    const attribution = this.getAttributionParams();

    this.gtag("event", "signup_complete", {
      event_category: "conversion",
      user_id: userId,
      time_to_convert_seconds: timeToConvert,
      ...attribution,
    });

    // Also fire the standard sign_up event for GA4 recommended events
    this.gtag("event", "sign_up", {
      method,
    });

    intercomService.trackEvent("signup_completed" as any, {
      time_to_convert_seconds: timeToConvert,
      ...attribution,
      timestamp: new Date().toISOString(),
    });

    posthogService.capture(TrackingEvents.AUTH_SIGNUP_COMPLETE, {
      user_id: userId,
      time_to_convert_seconds: timeToConvert ?? null,
      ...attribution,
    });

    // Clear signup start time
    sessionStorage.removeItem(STORAGE_KEYS.SIGNUP_START_TIME);
  }

  // ===================
  // LOGIN FUNNEL EVENTS
  // ===================

  /**
   * Track login funnel start (page load)
   */
  trackLoginStart(): void {
    sessionStorage.setItem(STORAGE_KEYS.LOGIN_START_TIME, new Date().toISOString());

    const attribution = this.getAttributionParams();

    this.gtag("event", "login_start", {
      event_category: "conversion_funnel",
      ...attribution,
    });

    intercomService.trackEvent("login_started" as any, {
      ...attribution,
      timestamp: new Date().toISOString(),
    });

    posthogService.capture(TrackingEvents.AUTH_LOGIN_START, attribution);
  }

  /**
   * Track login email submitted
   */
  trackLoginEmailSubmitted(email: string): void {
    const emailDomain = email.split("@")[1] || "unknown";

    this.gtag("event", "login_email_submitted", {
      event_category: "conversion_funnel",
      email_domain: emailDomain,
    });

    posthogService.capture(TrackingEvents.AUTH_LOGIN_EMAIL_SUBMITTED, {
      email_domain: emailDomain,
    });
  }

  /**
   * Track login success
   *
   * `method` identifies which auth path produced the successful login so
   * the PostHog / GA login funnel can slice by path without conflating
   * OTP and SSO users. Default stays `"email_otp"` so the existing OTP
   * callsite does not need to change. `"session_rehydrate"` is reserved
   * for a future cohort event -- do NOT emit it today; firing on every
   * page reload for an already-authenticated user would inflate
   * `auth_login_success` and break DAU math.
   */
  trackLoginSuccess(
    userId: string,
    userRole?: string,
    method: "email_otp" | "google_sso" | "session_rehydrate" = "email_otp"
  ): void {
    const startTime = sessionStorage.getItem(STORAGE_KEYS.LOGIN_START_TIME);
    const timeToLogin = startTime
      ? Math.round((Date.now() - new Date(startTime).getTime()) / 1000)
      : undefined;

    this.gtag("event", "login_success", {
      event_category: "conversion",
      user_id: userId,
      user_role: userRole || "investor",
      time_to_login_seconds: timeToLogin,
      method,
    });

    // Also fire the standard login event for GA4 recommended events
    this.gtag("event", "login", {
      method,
    });

    posthogService.capture(TrackingEvents.AUTH_LOGIN_SUCCESS, {
      user_id: userId,
      user_role: userRole || "investor",
      time_to_login_seconds: timeToLogin ?? null,
      method,
    });

    // Intercom already tracks login_success via intercomService.trackLoginSuccess()
    // Clear login start time
    sessionStorage.removeItem(STORAGE_KEYS.LOGIN_START_TIME);
  }

  // ===================
  // USER IDENTIFICATION
  // ===================

  /**
   * Identify user after authentication
   * Links anonymous sessions to authenticated user
   *
   * Extra properties (`is_internal`, etc.) flow through to the GA4
   * user_properties payload so they are filterable in GA reports.
   */
  identifyUser(
    userId: string,
    properties: {
      email?: string | null;
      role?: string;
      isProvider?: boolean;
      isAdmin?: boolean;
      providerId?: string;
      createdAt?: Date | string;
      is_internal?: boolean;
      [key: string]: unknown;
    }
  ): void {
    // Set user ID in GA
    this.setUserId(userId);

    // Calculate account age if createdAt provided
    let accountAgeDays: number | undefined;
    if (properties.createdAt) {
      const created = new Date(properties.createdAt);
      accountAgeDays = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
    }

    // Set user properties
    this.setUserProperties({
      user_role: properties.role || "investor",
      is_provider: properties.isProvider || false,
      is_admin: properties.isAdmin || false,
      provider_id: properties.providerId,
      signup_date: properties.createdAt
        ? new Date(properties.createdAt).toISOString().split("T")[0]
        : undefined,
      account_age_days: accountAgeDays,
      is_internal: properties.is_internal || false,
    });

    // Fire user_identified event to mark session linkage
    this.gtag("event", "user_identified", {
      event_category: "identity",
      user_role: properties.role || "investor",
      is_provider: properties.isProvider || false,
      is_admin: properties.isAdmin || false,
      account_age_days: accountAgeDays,
      is_internal: properties.is_internal || false,
    });
  }

  /**
   * Set the internal-user flag in GA4. Used so internal @runplutus.com
   * activity can be filtered out of reports without also dropping it
   * from GA (we keep GA capture on for debugging).
   */
  setInternalFlag(isInternal: boolean): void {
    this.setUserProperties({ is_internal: isInternal });
  }

  /**
   * Reset identity on logout. Clears GA user_id and resets user-level
   * properties (including is_internal) so the next anonymous or
   * login-start event on a shared device is not still attributed to
   * the previous user. Also resets PostHog identity, session id, super
   * properties, and any opt-out set during an internal-user session.
   * posthog.reset() already clears consent settings, so an explicit
   * opt_in_capturing() call here would only emit a noisy $opt_in event
   * on every logout.
   */
  reset(): void {
    if (this.gaMeasurementId) {
      // `user_id: null` clears the GA identity; resetting user properties
      // prevents stale `is_internal`, `user_role`, etc. bleeding into
      // subsequent sessions on shared devices.
      this.gtag("config", this.gaMeasurementId, {
        update: true,
        send_page_view: false,
        user_id: null,
      });
      this.gtag("set", "user_properties", {
        is_internal: false,
        user_role: null,
        is_provider: null,
        is_admin: null,
        provider_id: null,
      });
    }
    posthogService.reset();
  }

  // ===================
  // ENGAGEMENT TRACKING
  // ===================

  captureHomepageEvent(
    eventName: TrackingEventName,
    properties: Record<string, string | number | boolean | null> = {}
  ): void {
    const payload = {
      page: "homepage",
      page_version: "v7",
      ...properties,
    };
    this.gtag("event", eventName, payload);
    posthogService.capture(eventName, payload);
  }

  trackHomePageView(pageVersion = "v7"): void {
    if (typeof window === "undefined") return;
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_VIEWED, {
      path: `${window.location.pathname}${window.location.search}`,
      viewport_width: window.innerWidth,
      page_version: pageVersion,
    });
  }

  trackSectionView(sectionId: string, sectionName: string, sectionPosition: number, pageVersion = "v7"): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_SECTION_VIEWED, {
      section_id: sectionId,
      section_name: sectionName,
      section_position: sectionPosition,
      page_version: pageVersion,
    });
  }

  trackStoryPanelView(panelIndex: number, panelName: string): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_STORY_PANEL_VIEWED, {
      panel_index: panelIndex + 1,
      panel_name: panelName,
    });
  }

  trackProductView(viewIndex: number, viewName: string): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_PRODUCT_VIEWED, {
      product_view_index: viewIndex + 1,
      product_view_name: viewName,
    });
  }

  trackProductViewSelected(viewIndex: number, viewName: string): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_PRODUCT_SELECTED, {
      product_view_index: viewIndex + 1,
      product_view_name: viewName,
    });
  }

  trackProviderView(providerIndex: number, providerName: string): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_PROVIDER_VIEWED, {
      provider_index: providerIndex + 1,
      provider_name: providerName,
    });
  }

  trackCapabilitySelected(capabilityIndex: number, capabilityName: string): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_CAPABILITY_SELECTED, {
      capability_index: capabilityIndex + 1,
      capability_name: capabilityName,
    });
  }

  trackNavigationClick(label: string, section: string, destination: string, pageVersion = "v7"): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_NAVIGATION_CLICKED, {
      label,
      section,
      destination,
      page_version: pageVersion,
    });
  }

  trackMenuToggle(isOpen: boolean, pageVersion = "v7"): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_MENU_TOGGLED, {
      menu_state: isOpen ? "open" : "closed",
      page_version: pageVersion,
    });
  }

  trackDocumentOpen(documentName: string, section: string, destination: string, pageVersion = "v7"): void {
    this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_DOCUMENT_OPENED, {
      document_name: documentName,
      section,
      destination,
      page_version: pageVersion,
    });
  }

  /**
   * Track CTA button clicks.
   * Accepts the legacy positional form used across the app, or the
   * marketing-site payload that stamps campaign / intent / last-touch.
   */
  trackCTAClick(
    buttonName: string,
    location: string,
    destination?: string,
    extras?: Record<string, string | number | boolean | null>
  ): void;
  trackCTAClick(payload: CtaClickPayload): void;
  trackCTAClick(
    buttonNameOrPayload: string | CtaClickPayload,
    location?: string,
    destination?: string,
    extras?: Record<string, string | number | boolean | null>
  ): void {
    if (typeof buttonNameOrPayload === "object") {
      const { campaign, label, section, destination: dest, intent, pageVersion } = buttonNameOrPayload;
      const resolvedIntent = intent ?? ctaIntentFromCampaign(campaign);
      const versionProperties: Record<string, string> = pageVersion ? { page_version: pageVersion } : {};
      posthogService.registerForSession({
        last_cta_campaign: campaign,
        last_cta_label: label,
        last_cta_section: section,
        last_cta_intent: resolvedIntent,
        last_cta_destination: dest,
      });
      this.trackCTAClick(label, section, dest, {
        campaign,
        cta_id: campaign,
        intent: resolvedIntent,
        trigger: "user",
        ...versionProperties,
      });
      posthogService.capture(TrackingEvents.MARKETING_CTA_CLICKED, {
        campaign,
        cta_id: campaign,
        label,
        section,
        destination: dest,
        intent: resolvedIntent,
        trigger: "user",
        ...versionProperties,
      });
      const pathname = typeof window === "undefined" ? "" : window.location.pathname;
      if (pageVersion || pathname === "/" || pathname === "") {
        this.captureHomepageEvent(TrackingEvents.MARKETING_HOMEPAGE_CTA_CLICKED, {
          campaign,
          cta_id: campaign,
          label,
          section,
          destination: dest,
          intent: resolvedIntent,
          trigger: "user",
          ...versionProperties,
        });
      }
      return;
    }

    // `extras` lets callers add structured fields (cta_id, source_surface,
    // signup_type, etc.) without forking the emit into a separate
    // trackingService.track() call, which would only write to PostHog.
    // Keeping a single entry point preserves the GA + PostHog fan-out
    // and avoids the provider-CTA regression where trackingService.track
    // bypassed GA entirely.
    this.gtag("event", "cta_click", {
      event_category: "engagement",
      button_name: buttonNameOrPayload,
      click_location: location,
      destination: destination,
      ...extras,
    });
    posthogService.capture(TrackingEvents.MARKETING_CTA_CLICK, {
      button_name: buttonNameOrPayload,
      click_location: location,
      destination: destination ?? null,
      ...extras,
    });
  }

  /**
   * Track scroll depth milestones
   */
  trackScrollDepth(depth: 25 | 50 | 75 | 100, pagePath: string): void {
    this.gtag("event", "scroll_depth", {
      event_category: "engagement",
      scroll_depth_percent: depth,
      page_path: pagePath,
    });
    posthogService.capture(TrackingEvents.MARKETING_SCROLL_DEPTH, {
      scroll_depth_percent: depth,
      page_path: pagePath,
    });
  }

  /**
   * Track outbound link clicks
   */
  trackOutboundLink(url: string, linkText?: string): void {
    this.gtag("event", "outbound_link", {
      event_category: "engagement",
      link_url: url,
      link_text: linkText,
    });
    posthogService.capture(TrackingEvents.MARKETING_OUTBOUND_CLICK, {
      link_url: url,
      link_text: linkText ?? null,
    });
  }

  /**
   * Track form field interactions (for abandonment analysis)
   */
  trackFormFieldInteraction(
    formName: string,
    fieldName: string,
    action: "focus" | "blur" | "change"
  ): void {
    this.gtag("event", "form_field_interaction", {
      event_category: "form",
      form_name: formName,
      field_name: fieldName,
      interaction_type: action,
    });
  }

  /**
   * Track error events
   */
  trackError(errorMessage: string, errorLocation: string, errorCode?: string): void {
    this.gtag("event", "error", {
      event_category: "error",
      error_message: errorMessage,
      error_location: errorLocation,
      error_code: errorCode,
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
